import { Component, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-calendar-modal',
    templateUrl: './calendar-modal.component.html',
    styleUrl: './calendar-modal.component.scss'
})
export class CalendarModalComponent {
    private readonly activeModal = inject(NgbActiveModal);
    private readonly translateService = inject(TranslateService);

    public dates: Date[] = [];
    public agreementName: string = '';
    public agreementUrl: string | null = null;

    public close(): void {
        this.activeModal.close();
    }

    public isDateInPast(date: Date): boolean {
        const compareDate = new Date(date);
        const today = new Date();
        compareDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return compareDate < today;
    }

    public getGoogleCalendarUrl(date: Date): string {
        const base = this.translateService.instant('FEATURES.AGREEMENTS.CALENDAR_EVENT_SUMMARY') as string;
        const summary = this.agreementName ? `${base} - ${this.agreementName}` : base;
        const goToLabel = this.translateService.instant('FEATURES.AGREEMENTS.CALENDAR_EVENT_DESCRIPTION') as string;
        const description = this.agreementUrl ? `${goToLabel}: ${this.agreementUrl}` : '';

        const dateStr = this.formatDateForICal(date);
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        const nextDayStr = this.formatDateForICal(nextDay);

        const params = new URLSearchParams({
            action: 'TEMPLATE',
            text: summary,
            dates: `${dateStr}/${nextDayStr}`,
            details: description
        });
        return `https://calendar.google.com/calendar/render?${params.toString()}`;
    }

    public getOutlookCalendarUrl(date: Date): string {
        const base = this.translateService.instant('FEATURES.AGREEMENTS.CALENDAR_EVENT_SUMMARY') as string;
        const summary = this.agreementName ? `${base} - ${this.agreementName}` : base;
        const goToLabel = this.translateService.instant('FEATURES.AGREEMENTS.CALENDAR_EVENT_DESCRIPTION') as string;
        const description = this.agreementUrl ? `${goToLabel}: ${this.agreementUrl}` : '';

        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        const fmt = (d: Date) => {
            const y = d.getFullYear();
            const m = (d.getMonth() + 1).toString().padStart(2, '0');
            const dd = d.getDate().toString().padStart(2, '0');
            return `${y}-${m}-${dd}`;
        };

        const params = new URLSearchParams({
            subject: summary,
            startdt: fmt(date),
            enddt: fmt(nextDay),
            body: description,
            allday: 'true'
        });
        return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
    }

    public downloadSingleEvent(date: Date, index: number): void {
        const base = this.translateService.instant('FEATURES.AGREEMENTS.CALENDAR_EVENT_SUMMARY') as string;
        const summary = this.agreementName ? `${base} - ${this.agreementName}` : base;
        const goToLabel = this.translateService.instant('FEATURES.AGREEMENTS.CALENDAR_EVENT_DESCRIPTION') as string;
        const description = this.agreementUrl ? `${goToLabel}: ${this.agreementUrl}` : undefined;

        const dateStr = this.formatDateForICal(date);
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        const nextDayStr = this.formatDateForICal(nextDay);

        const lines = [
            'BEGIN:VEVENT',
            `UID:agreement-reminder-${index}-${dateStr}@agreementportal`,
            `DTSTART;VALUE=DATE:${dateStr}`,
            `DTEND;VALUE=DATE:${nextDayStr}`,
            `SUMMARY:${summary}`
        ];
        if (description) {
            lines.push(`DESCRIPTION:${description}`);
        }
        lines.push('END:VEVENT');

        const ical = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//AgreementPortal//Reminders//EN',
            'CALSCALE:GREGORIAN',
            'METHOD:REQUEST',
            lines.join('\r\n'),
            'END:VCALENDAR'
        ].join('\r\n');

        this.triggerDownload(ical, `reminder-${dateStr}.ics`);
    }

    public downloadAllCalendar(): void {
        const base = this.translateService.instant('FEATURES.AGREEMENTS.CALENDAR_EVENT_SUMMARY') as string;
        const summary = this.agreementName ? `${base} - ${this.agreementName}` : base;
        const goToLabel = this.translateService.instant('FEATURES.AGREEMENTS.CALENDAR_EVENT_DESCRIPTION') as string;
        const description = this.agreementUrl ? `${goToLabel}: ${this.agreementUrl}` : undefined;

        const events = this.dates.map((date, i) => {
            const dateStr = this.formatDateForICal(date);
            const nextDay = new Date(date);
            nextDay.setDate(nextDay.getDate() + 1);
            const nextDayStr = this.formatDateForICal(nextDay);
            const lines = [
                'BEGIN:VEVENT',
                `UID:agreement-reminder-${i}-${dateStr}@agreementportal`,
                `DTSTART;VALUE=DATE:${dateStr}`,
                `DTEND;VALUE=DATE:${nextDayStr}`,
                `SUMMARY:${summary}`
            ];
            if (description) {
                lines.push(`DESCRIPTION:${description}`);
            }
            lines.push('END:VEVENT');
            return lines.join('\r\n');
        });

        const ical = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//AgreementPortal//Reminders//EN',
            'CALSCALE:GREGORIAN',
            'METHOD:PUBLISH',
            ...events,
            'END:VCALENDAR'
        ].join('\r\n');

        this.triggerDownload(ical, 'reminders.ics');
    }

    private formatDateForICal(date: Date): string {
        const y = date.getFullYear().toString();
        const m = (date.getMonth() + 1).toString().padStart(2, '0');
        const d = date.getDate().toString().padStart(2, '0');
        return `${y}${m}${d}`;
    }

    private triggerDownload(content: string, filename: string): void {
        const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        URL.revokeObjectURL(url);
    }
}
